import React from 'react';
import { Box, Card, CardContent, Skeleton, Stack } from '@mui/material';
import { LoadingSpinner } from '../ui/IconComponents';

interface UniversalLoadingProps {
  variant?: 'auth' | 'dashboard' | 'admin';
  message?: string;
}

// ========================================
// SUB-COMPONENTS FOR BETTER ORGANIZATION
// ========================================

/**
 * Auth loading layout with centered card design
 */
const AuthLoadingLayout: React.FC = () => (
  <Box sx={{
    minHeight: '100vh',
    width: '100vw',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(to bottom right, rgba(246, 154, 126, 0.3), rgba(147, 155, 236, 0.3))'
  }}>
    <Card sx={{
      width: '100%',
      maxWidth: 448,
      borderRadius: 4,
      boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
      border: '1px solid',
      borderColor: 'grey.100'
    }}>
      <CardContent sx={{
        p: 5,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center'
      }}>
        {/* Page title skeleton */}
        <Skeleton 
          variant="text" 
          width={300} 
          height={40} 
          sx={{ mb: 3 }} 
        />
        
        {/* Form fields skeletons */}
        <Stack spacing={3} sx={{ width: '100%' }}>
          <Skeleton variant="rectangular" width="100%" height={56} />
          <Skeleton variant="rectangular" width="100%" height={56} />
          <Skeleton variant="rectangular" width="100%" height={48} />
        </Stack>
        
        {/* Loading indicator */}
        <Box sx={{ mt: 4 }}>
          <LoadingSpinner size="lg" />
        </Box>
        
        {/* Loading message skeleton */}
        <Skeleton 
          variant="text" 
          width={120} 
          height={20} 
          sx={{ mt: 2 }} 
        />
      </CardContent>
    </Card>
  </Box>
);

/**
 * Header skeleton for dashboard and admin layouts
 */
const HeaderSkeleton: React.FC<{ isAdmin: boolean }> = ({ isAdmin }) => (
  <Box className="bg-white shadow-sm border-b border-gray-200 p-4">
    <div className="flex items-center justify-between max-w-7xl mx-auto">
      <Skeleton 
        variant="text" 
        width={isAdmin ? 200 : 150} 
        height={32} 
      />
      <div className="flex items-center gap-4">
        <Skeleton variant="text" width={150} height={24} />
        <Skeleton variant="rectangular" width={100} height={36} />
      </div>
    </div>
  </Box>
);

/**
 * Page title skeleton section
 */
const PageTitleSkeleton: React.FC<{ isAdmin: boolean }> = ({ isAdmin }) => (
  <div className="mb-8">
    <Skeleton 
      variant="text" 
      width={isAdmin ? 150 : 200} 
      height={40} 
      className="mb-2" 
    />
    <Skeleton variant="text" width={300} height={20} />
  </div>
);

/**
 * Admin-specific content skeleton
 */
const AdminContentSkeleton: React.FC = () => (
  <>
    {/* Statistics cards */}
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
      {Array.from({ length: 3 }, (_, index) => (
        <Card key={`admin-stat-card-${index}`}>
          <CardContent>
            <Skeleton variant="text" width={150} height={28} className="mb-2" />
            <Skeleton variant="text" width={80} height={48} />
          </CardContent>
        </Card>
      ))}
    </div>
    
    {/* Data table */}
    <Card>
      <CardContent className="p-0">
        <div className="p-6 border-b">
          <Skeleton variant="text" width={200} height={32} />
        </div>
        <Stack spacing={3} className="p-6">
          {Array.from({ length: 5 }, (_, index) => (
            <div key={`admin-table-row-${index}`} className="flex items-center gap-4">
              <Skeleton variant="circular" width={40} height={40} />
              <div className="flex-1">
                <Skeleton variant="text" width="60%" height={20} />
                <Skeleton variant="text" width="40%" height={16} />
              </div>
              <Skeleton variant="rectangular" width={80} height={32} />
            </div>
          ))}
        </Stack>
      </CardContent>
    </Card>
  </>
);

/**
 * Dashboard-specific content skeleton
 */
const DashboardContentSkeleton: React.FC = () => (
  <>
    {/* Control panel */}
    <Card className="mb-6">
      <CardContent className="p-6">
        <Stack spacing={4}>
          {/* Model selector */}
          <div>
            <Skeleton variant="text" width={120} height={24} className="mb-2" />
            <Skeleton variant="rectangular" width="100%" height={56} />
          </div>
          
          {/* Prompt input */}
          <div>
            <Skeleton variant="text" width={100} height={24} className="mb-2" />
            <Skeleton variant="rectangular" width="100%" height={120} />
          </div>
          
          {/* Action button */}
          <div className="flex justify-end">
            <Skeleton variant="rectangular" width={135} height={49} />
          </div>
        </Stack>
      </CardContent>
    </Card>
    
    {/* Video gallery */}
    <Card>
      <CardContent className="p-6">
        <div className="flex items-center justify-between mb-6">
          <Skeleton variant="text" width={150} height={32} />
          <Skeleton variant="rectangular" width={120} height={36} />
        </div>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {Array.from({ length: 8 }, (_, index) => (
            <div key={`video-skeleton-${index}`} className="space-y-3">
              <Skeleton 
                variant="rectangular" 
                width="100%" 
                height={200} 
                className="rounded-lg" 
              />
              <Skeleton variant="text" width="80%" height={20} />
              <Skeleton variant="text" width="60%" height={16} />
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  </>
);

/**
 * Loading message footer
 */
const LoadingMessage: React.FC<{ message: string }> = ({ message }) => (
  <div className="flex items-center justify-center mt-8">
    <LoadingSpinner size="md" className="mr-2" />
    <span className="text-gray-600">{message}</span>
  </div>
);

// ========================================
// MAIN COMPONENT
// ========================================

/**
 * UniversalLoading - A unified loading component for all app states
 * 
 * This component replaces separate AuthLoading, DashboardLoading, and AdminLoading
 * components with a single, variant-based implementation that's easier to maintain.
 * 
 * @param variant - The layout type: 'auth', 'dashboard', or 'admin'
 * @param message - Custom loading message to display
 */
const UniversalLoading: React.FC<UniversalLoadingProps> = ({ 
  variant = 'dashboard',
  message = 'Loading...'
}) => {
  // Auth variant uses a different layout structure
  if (variant === 'auth') {
    return <AuthLoadingLayout />;
  }

  // Dashboard and admin variants share the same layout structure
  const isAdmin = variant === 'admin';

  return (
    <Box className="w-full min-h-screen bg-gray-50">
      <HeaderSkeleton isAdmin={isAdmin} />
      
      <Box className="max-w-7xl mx-auto p-6">
        <PageTitleSkeleton isAdmin={isAdmin} />
        
        {isAdmin ? <AdminContentSkeleton /> : <DashboardContentSkeleton />}
        
        <LoadingMessage message={message} />
      </Box>
    </Box>
  );
};

export default UniversalLoading;
